// GoogleReviews.js — auto-rotating Google reviews carousel
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

// Star renderer
const Stars = ({ n = 5 }) => (
  <div style={{ display: 'flex', gap: '2px' }}>
    {[1,2,3,4,5].map(i => (
      <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i <= n ? '#F5A800' : '#333'}>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ))}
  </div>
);

// Google G logo
const GoogleG = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Default reviews to show until admin adds Google reviews via admin panel
const DEFAULT_REVIEWS = [
  { _id:'1', name:'Rahul Sharma', location:'Tumakuru', rating:5, review:'Wedoo Interiors transformed our 3BHK completely. The modular kitchen they designed is stunning — functionality and beauty combined. Very professional team.', source:'google', isVerified:true, projectType:'Modular Kitchen' },
  { _id:'2', name:'Priya Menon', location:'Tumakuru', rating:5, review:'Excellent work on our false ceiling and interior design. They finished on time, the quality is superb, and the 3D designs they showed us were exactly what we wanted.', source:'google', isVerified:true, projectType:'False Ceiling' },
  { _id:'3', name:'Suresh Kumar', location:'Tumakuru', rating:5, review:'Best interior designers in Tumakuru! Our home looks like it\'s straight out of a magazine. Great communication throughout the project.', source:'google', isVerified:true, projectType:'Complete Interior' },
  { _id:'4', name:'Kavitha Reddy', location:'Tumakuru', rating:5, review:'Very trustworthy and professional. They completed the aluminium and glass work for our office within the promised timeline. Highly recommended.', source:'google', isVerified:true, projectType:'Aluminium & Glass' },
  { _id:'5', name:'Manjunath Gowda', location:'Tumakuru', rating:5, review:'Wedoo Interiors did our home lighting and it looks phenomenal. Every room has the perfect ambiance. The team was polite and clean in their work.', source:'google', isVerified:true, projectType:'Home Lighting' },
  { _id:'6', name:'Deepa Naik', location:'Tumakuru', rating:5, review:'Affordable prices, great quality and zero delays. Our modular kitchen is beautiful. My friends all ask who did the work!', source:'google', isVerified:true, projectType:'Modular Kitchen' },
];

export default function GoogleReviews() {
  const [reviews,  setReviews]  = useState([]);
  const [current,  setCurrent]  = useState(0);
  const [avg,      setAvg]      = useState(5.0);
  const [total,    setTotal]    = useState(0);
  const autoRef = useRef(null);

  useEffect(() => {
    api.get('/reviews?featured=true').then(r => {
      const raw = r.data;
      // Handle both {reviews:[]} and [] response formats
      const data = Array.isArray(raw) ? raw : (Array.isArray(raw?.reviews) ? raw.reviews : []);
      const avgR = typeof raw?.avgRating === 'number' ? raw.avgRating : 5.0;
      const tot  = typeof raw?.total === 'number' ? raw.total : data.length;
      if (data.length >= 2) {
        setReviews(data); setAvg(avgR); setTotal(tot);
      } else {
        setReviews(DEFAULT_REVIEWS); setAvg(5.0); setTotal(DEFAULT_REVIEWS.length);
      }
    }).catch(() => { setReviews(DEFAULT_REVIEWS); setAvg(5.0); setTotal(DEFAULT_REVIEWS.length); });
  }, []);

  useEffect(() => {
    if (reviews.length < 2) return undefined;   // must be explicit undefined
    autoRef.current = setInterval(() => setCurrent(p => (p + 1) % Math.max(1, reviews.length - 2)), 4000);
    return () => clearInterval(autoRef.current);
  }, [reviews.length]);

  const visible = reviews.slice(current, current + 3);
  if (visible.length < 3 && reviews.length >= 3) {
    const need = 3 - visible.length;
    for (let i = 0; i < need; i++) visible.push(reviews[i]);
  }

  if (!reviews.length) return null;

  return (
    <section className="section-pad" style={{ background: '#0a0a0a', borderTop: '1px solid rgba(245,168,0,0.1)', borderBottom: '1px solid rgba(245,168,0,0.1)' }}>
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <p className="tag-label" style={{ justifyContent: 'center', marginBottom: '0.75rem' }}>Client Stories</p>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem,4vw,2.8rem)', fontWeight: 400, color: '#fff', marginBottom: '0.5rem' }}>
            Loved By <em style={{ color: '#F5A800' }}>Our Customers</em>
          </h2>
          {/* Aggregate rating */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
            <GoogleG />
            <Stars n={Math.round(avg)} />
            <span style={{ color: '#F5A800', fontWeight: '600', fontSize: '1.1rem' }}>{avg}</span>
            <span style={{ color: '#888', fontSize: '0.82rem' }}>({total} Google reviews)</span>
          </div>
        </div>

        {/* Cards */}
        <div style={{ position: 'relative' }}>
          <div className="grid-3" style={{ gap: '1.25rem', overflow: 'hidden' }}>
            {(visible.length ? visible : reviews.slice(0, 3)).map((r, i) => (
              <div key={`${r._id}-${i}`} style={{
                background: '#141414', border: '1px solid #222',
                padding: '1.75rem', position: 'relative',
                transition: 'all 0.4s ease',
                animation: 'fadeUp 0.5s ease both',
                animationDelay: `${i * 0.1}s`,
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,168,0,0.3)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#222'; e.currentTarget.style.transform = 'none'; }}>
                {/* Quote mark */}
                <div style={{ position: 'absolute', top: '1rem', right: '1.25rem' }}>
                  <GoogleG />
                </div>

                {/* Stars */}
                <Stars n={r.rating} />

                {/* Review text */}
                <p style={{ color: '#ccc', fontSize: '0.85rem', lineHeight: '1.8', margin: '1rem 0 1.25rem', minHeight: '80px' }}>
                  "{r.review}"
                </p>

                {/* Reviewer */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid #1e1e1e' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                    background: `linear-gradient(135deg, #F5A800, #C48600)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: '600', fontSize: '1rem', color: '#000',
                  }}>
                    {r.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: '#fff', fontWeight: '500', fontSize: '0.85rem' }}>{r.name}</span>
                      {r.isVerified && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#4285F4">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {r.location && <span style={{ color: '#666', fontSize: '0.72rem' }}>📍 {r.location}</span>}
                      {r.projectType && <span style={{ color: '#F5A800', fontSize: '0.68rem' }}>• {r.projectType}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Prev / Next */}
          {reviews.length > 3 && (
            <>
              <button onClick={() => { clearInterval(autoRef.current); setCurrent(p => Math.max(0, p - 1)); }} style={{
                position: 'absolute', left: '-2rem', top: '50%', transform: 'translateY(-50%)',
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'rgba(245,168,0,0.1)', border: '1px solid rgba(245,168,0,0.3)',
                color: '#F5A800', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>‹</button>
              <button onClick={() => { clearInterval(autoRef.current); setCurrent(p => (p + 1) % Math.max(1, reviews.length - 2)); }} style={{
                position: 'absolute', right: '-2rem', top: '50%', transform: 'translateY(-50%)',
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'rgba(245,168,0,0.1)', border: '1px solid rgba(245,168,0,0.3)',
                color: '#F5A800', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>›</button>
            </>
          )}
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '2rem' }}>
          {reviews.slice(0, Math.max(1, reviews.length - 2)).map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} style={{
              width: i === current ? '24px' : '8px', height: '4px', borderRadius: '2px', border: 'none', cursor: 'pointer',
              background: i === current ? '#F5A800' : '#333', transition: 'all 0.3s',
            }} />
          ))}
        </div>

        {/* Write review CTA */}
        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <a href="https://g.page/r/XXXXXXXXXX/review" target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', color: '#888', fontSize: '0.8rem', border: '1px solid #2a2a2a', borderRadius:'var(--radius)', padding: '0.6rem 1.25rem', transition: 'all 0.3s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,168,0,0.3)'; e.currentTarget.style.color = '#F5A800'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}>
            <GoogleG /> Leave a Google Review
          </a>
        </div>
      </div>

      <style>{`@keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </section>
  );
}
